import { apiErrorMessage } from 'src/app/core/api-error.utils';
import {
  Component,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { DialogService } from '@sui/dialog';
import { ExportTemplate } from '../../../../core/models/core.models';
import { ExportTemplateApiService } from 'src/app/api/services/export-template-api.service';
import { ExportTemplatesEditorDialogComponent } from '../../../dialogs/export-templates-editor-dialog/export-templates-editor-dialog.component';
import { NomOutputFormat } from '../../../../core/constants';
import { environment } from '@env/environment';
import { SuiButtonComponent } from '@sui/button';
import { LoadingComponent } from '../../../../shared/components/loading/loading.component';
import { TranslatePipe } from '@ngx-translate/core';
import { SuiTab, SuiTabsComponent } from '@sui/tabs';

interface TemplateFormatTab {
  id: NomOutputFormat;
  label: string;
}
@Component({
  selector: 'app-export-templates',
  templateUrl: './export-templates.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './export-templates.component.scss',
  imports: [
    SuiTabsComponent,
    SuiButtonComponent,
    LoadingComponent,
    TranslatePipe,
  ],
})
export class ExportTemplatesComponent {
  @Input() companyId: string = '';

  formats: TemplateFormatTab[] = [
    { id: NomOutputFormat.DOCX, label: 'DocX' },
    { id: NomOutputFormat.HTML, label: 'HTML' },
  ];

  activeFormat: TemplateFormatTab = this.formats[0];

  activeFormatId: NomOutputFormat = NomOutputFormat.DOCX;

  templates: ExportTemplate[] = [];
  loading = false;
  error: string | null = null;

  protected dialogService: any = inject(DialogService);

  constructor(private exportTemplateApi: ExportTemplateApiService) {}

  ngOnInit(): void {
    this.loadTemplates();
  }

  protected get tabItems(): SuiTab[] {
    return this.formats.map((format) => ({
      value: String(format.id),
      label: format.label,
    }));
  }

  protected get activeTabValue(): string {
    return String(this.activeFormatId);
  }

  protected onTabChange(value: string): void {
    const format = this.formats.find((f) => String(f.id) === value);
    if (format) this.onTabClick(format);
  }

  onTabClick(format: TemplateFormatTab): void {
    if (this.activeFormatId === format.id) {
      return;
    }
    this.activeFormatId = format.id;
    this.activeFormat = format;
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.loading = true;
    this.error = null;

    this.exportTemplateApi
      .listTemplates(this.companyId, this.activeFormatId)
      .subscribe({
        next: (templates) => {
          this.templates = templates;
          this.loading = false;
        },
        error: (err: unknown) => {
          this.error = apiErrorMessage(err, 'Failed to load templates');
          this.loading = false;
        },
      });
  }

  trackByTemplateId(_index: number, item: ExportTemplate): number {
    return item.id;
  }

  getTemplateThumbnailUrl(template: ExportTemplate): string {
    return (
      environment.apiBaseUrl +
      `/export_template/get_template_thumbnail?template_id=${template.id}`
    );
  }

  getFormatLabel(template: ExportTemplate): string {
    switch (template.output_format.id) {
      case NomOutputFormat.DOCX:
        return 'DocX';
      case NomOutputFormat.HTML:
        return 'HTML';
      default:
        return String(template.output_format.id);
    }
  }

  protected createExportTemplate(): void {
    this.dialogService.openTabsDialog(ExportTemplatesEditorDialogComponent, {
      data: { exportTemplate: null, companyId: this.companyId },
      closeOnClick: false,
      closeOnEscape: true,
      height: '70vh',
      minHeight: 800,
      maxHeight: 1020,
    });
  }
}
